import { inject, Inject, Injectable, Renderer2, RendererFactory2, TransferState } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { StructuredDataTag } from 'src/app/service/meta/structured.data'
import { DOCUMENT } from '@angular/common'
import { ED_INJECT_DOMAIN, ED_INJECT_HOST, ED_STORE_DOMAIN, ED_STORE_HOST } from 'src/server/server.data'
import { RuntimeService } from 'src/app/service/runtime.service'
import punycode from 'punycode'
import { PageContext, SalePageType } from 'src/app/service/redirector.service'

type MetaKeys = {
  [key in MetaKeyId]: Array<{ name: string } | { property: string }>
}

export type MetaKeyId = 'url' | 'title' | 'release' | 'desc' | 'image'
export const metaKeys: MetaKeys = {
  url: [{ property: 'og:url' }],
  title: [{ property: 'og:title' }, { property: 'twitter:title' }],
  release: [{ property: 'article:published_time' }, { property: 'article:modified_time' }, { property: 'og:updated_time' }],
  desc: [{ name: 'description' }, { name: 'og:description' }, { name: 'twitter:description' }],
  image: [{ property: 'og:image' }, { name: 'twitter:image' }],
}

@Injectable()
export class MetaService {
  protected url: string
  protected domain: string
  protected encodedDomain: string
  protected host: string
  protected renderer: Renderer2
  protected pageSD!: StructuredDataTag

  constructor (
    protected titleService: Title,
    protected meta: Meta,
    protected router: Router,
    protected route: ActivatedRoute,
    protected transferState: TransferState,
    protected runtimeService: RuntimeService,
    protected rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) protected document: Document,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null)

    if (runtimeService.isClient()) {
      this.host = this.transferState.get(ED_STORE_HOST, '')
      this.domain = this.transferState.get(ED_STORE_DOMAIN, '')
    } else {
      this.host = inject(ED_INJECT_HOST)
      this.domain = inject(ED_INJECT_DOMAIN)
      this.transferState.set(ED_STORE_HOST, this.host)
      this.transferState.set(ED_STORE_DOMAIN, this.domain)
    }

    this.encodedDomain = punycode.toUnicode(this.domain)

    this.url = 'https://' + this.host
  }

  setDomain (context: PageContext<SalePageType>) {
    const that = this
    that.addMeta('url', this.router.url)

    const newPageSD = {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      'url': this.url + this.router.url,
      'name': $localize`${this.getEncodedDomain()} steht zum Verkauf`,
    }

    if (this.pageSD) {
      this.pageSD.update(newPageSD)
    } else {
      this.pageSD = new StructuredDataTag('page-website', newPageSD, that.renderer, that.document)
    }

    that.addMeta('image' as MetaKeyId, 'https://elitedomains.de/static/images/brand/open-graph.jpg')
    that.addMeta('title' as MetaKeyId, $localize`${this.getEncodedDomain()} steht zum Verkauf`)
    that.addMeta(
      'desc' as MetaKeyId,
      context.config.type === 'paynow'
        ? $localize`Die Domain ${this.getEncodedDomain()} steht zum Verkauf. Sie können diese Domain jetzt per Sofortkauf zum Festpreis mit dem Treuhandservice von ELITEDOMAINS kaufen.`
        // TODO Jörn: Hier die MetaDesc für 4Sale einfügen
        : $localize`Die Domain ${this.getEncodedDomain()} steht zum Verkauf. Sie können diese Domain jetzt per Sofortkauf zum Festpreis mit dem Treuhandservice von ELITEDOMAINS kaufen.`,
      )

    this.meta.updateTag({ property: 'og:type', content: 'website' })
    this.meta.updateTag({ property: 'og:locale', content: 'en_US' }) // FIXME
  }

  setSeo(canonicalUrl: string, noindex: boolean) {
    const link = this.document.createElement('link')
    link.setAttribute('rel', 'canonical')
    link.setAttribute('href', canonicalUrl)
    this.document.head.appendChild(link)

    if (noindex) {
      this.meta.addTag({ property: 'robots', content: 'noindex' })
    }
  }

  addMeta (
    id: MetaKeyId,
    content: string,
  ) {
    const that = this
    metaKeys[id].forEach(proto => {
      const key = Object.keys(proto)[0]
      const tag = {
        ...proto,
        content,
      }

      that.meta.updateTag(
        tag,
        // @ts-ignore
        [key, '="', tag[key], '"'].join(''),
      )
    })

    if (id === 'title') {
      that.titleService.setTitle(content)
    }
  }

  getDomain () {
    return this.domain
  }

  getEncodedDomain () {
    if (this.domain.match(/--/g)) {
      return this.encodedDomain
    }
    return this.domain
  }

  getHost () {
    return this.host
  }
}
